import React, { useMemo } from "react";
import { createTheme as criaTemaV4, ThemeProvider as ProviderV4 } from "@material-ui/core/styles";
import { createTheme as criaTemaV5, ThemeProvider as ProviderV5 } from "@mui/material/styles";

import { useThemes } from "./theme";
import { getCurrentThemeStyle } from "../services/functions";

/**
 * O projeto usa Material UI em duas versoes (v4 e v5) e nenhuma delas tinha
 * tema configurado, entao `color="primary"` caia no azul padrao da
 * biblioteca. Era dai que vinha o azul das abas e dos botoes espalhados pelo
 * sistema: dezessete telas ainda usam <Tabs indicatorColor="primary">.
 *
 * Em vez de reescrever cada uma, a cor primaria das duas versoes passa a ser
 * a da marca. A paleta sai do mesmo Theme.js dos componentes proprios, entao
 * acompanha o departamento (Banho e Tosa laranja, Clinica azul) em vez de
 * fixar uma cor.
 */
export function MuiBrandTheme({ children }) {
  const { theme } = useThemes();

  const paleta = useMemo(() => {
    const cores = getCurrentThemeStyle().colors;

    return {
      primary: {
        light: cores.scale[300],
        main: cores.scale[500],
        dark: cores.scale[600],
        contrastText: "#ffffff",
      },
      secondary: {
        light: cores.scale[100],
        main: cores.scale[600],
        dark: cores.scale[700],
        contrastText: "#ffffff",
      },
    };
    // `theme` entra aqui porque trocar de departamento troca a paleta
    // eslint-disable-next-line
  }, [theme]);

  /**
   * Cabecalho laranja tambem nas tabelas que ainda sao do Material UI. Sao
   * cerca de 35 telas; reescrever todas para o Table proprio seria demorado e
   * arriscado, entao pelo menos o cabecalho fica igual ao do resto do sistema.
   */
  const cabecalho = useMemo(
    () => ({
      backgroundColor: paleta.primary.main,
      color: "#ffffff",
      fontSize: 13,
      fontWeight: "bold",
      letterSpacing: "0.2px",
      whiteSpace: "nowrap",
      padding: "8px 10px",
      borderBottom: "none",
    }),
    [paleta]
  );

  /*
   * O ordenador do Material UI nasce cinza-escuro, pensado para cabecalho
   * claro: sobre o laranja ele sumia, e a seta de direcao mais ainda. Como
   * TableSortLabel so existe dentro de cabecalho de tabela, e todo cabecalho
   * aqui e laranja, branco vale em todos eles.
   */
  const ordenador = useMemo(
    () => ({
      root: {
        color: "#ffffff",
        "&:hover": { color: "#ffffff" },
        "&:focus": { color: "#ffffff" },
      },
      active: { color: "#ffffff" },
      icon: { color: "#ffffff", opacity: 0.85 },
    }),
    []
  );

  const temaV4 = useMemo(
    () =>
      criaTemaV4({
        palette: paleta,
        overrides: {
          MuiTableCell: { head: cabecalho },
          MuiTableSortLabel: ordenador,
        },
      }),
    [paleta, cabecalho, ordenador]
  );

  const temaV5 = useMemo(
    () =>
      criaTemaV5({
        palette: paleta,
        components: {
          MuiTableCell: { styleOverrides: { head: cabecalho } },
          MuiTableSortLabel: { styleOverrides: ordenador },
        },
      }),
    [paleta, cabecalho, ordenador]
  );

  return (
    <ProviderV4 theme={temaV4}>
      <ProviderV5 theme={temaV5}>{children}</ProviderV5>
    </ProviderV4>
  );
}
